import {
  getObject,
  getLeoUuid,
  LeoUUID,
  getBoolean,
  getString,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { RespondentColumnName, RespondentColumnNameDTO } from "../types/respondentColumnName";
/* eslint-disable import/export */
export abstract class ValidateUniqueColumnAndUpdateRespondentRPC implements
  LeoRPC<ValidateUniqueColumnAndUpdateRespondentRPC.Request, ValidateUniqueColumnAndUpdateRespondentRPC.Response, ValidateUniqueColumnAndUpdateRespondentRPC.Errors.Errors>
{
  abstract execute(
    request: ValidateUniqueColumnAndUpdateRespondentRPC.Request
  ): Promise<LeoRPCResult<ValidateUniqueColumnAndUpdateRespondentRPC.Response, ValidateUniqueColumnAndUpdateRespondentRPC.Errors.Errors>>;
}

export namespace ValidateUniqueColumnAndUpdateRespondentRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  
  export namespace RequestEnums {
    export namespace UploadType {
      export interface FirstDTO {
        case: string;
        uniqueColumn: RespondentColumnNameDTO;
      }
      
      export class First {
        readonly isUploadTypeFirst: boolean = true;
      
        readonly uniqueColumn: RespondentColumnName;
      
      
        constructor(
        uniqueColumn: RespondentColumnName
        ) {
          this.uniqueColumn = uniqueColumn;
        }
      
        static fromDTO(dto: FirstDTO): First {
          const uniqueColumn = RespondentColumnName.fromDTO(getObject(dto, "uniqueColumn", InvalidRequestUploadTypeFirstUniqueColumnError));
          return new First(
            uniqueColumn
          );
        }
      
        toDTO(): FirstDTO {
          const dto: FirstDTO = {
            case: "FIRST",
              uniqueColumn: this.uniqueColumn.toDTO()
          };
          return dto;
        }
        }
      
      First.prototype.toString = function toString(): string {
        return `UploadType.FIRST(uniqueColumn='${this.uniqueColumn}')`;
      };
      
      export interface SubsequentDTO {
        case: string;
      }
      
      export class Subsequent {
        readonly isUploadTypeSubsequent: boolean = true;
      
      
      
      
      
        toDTO(): SubsequentDTO {
          const dto: SubsequentDTO = {
            case: "SUBSEQUENT",
          };
          return dto;
        }
        }
      
      Subsequent.prototype.toString = function toString(): string {
        return `UploadType.SUBSEQUENT()`;
      };
      
    
      export type UploadType = First | Subsequent;
    
      export type UploadTypeDTO = FirstDTO | SubsequentDTO;
    
      export const fromDTO = (dto: UploadTypeDTO): UploadType => {
        switch (dto.case) {
          case "FIRST":
              return First.fromDTO(dto as FirstDTO);
          case "SUBSEQUENT":
              return new Subsequent();
          default:
            throw new InvalidRequestUploadTypeError(
                `Case ${dto.case} is not valid case of UploadType.`
            );
        }
      };
    }
    
  }
  export interface RequestDTO {
    uploadType: RequestEnums.UploadType.UploadTypeDTO;
    projectId: string;
    documentId: string;
    isLive: boolean;
    respectUniqueColumn: boolean;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isValidateUniqueColumnAndUpdateRespondentRPCRequest: boolean = true;
  
    readonly uploadType: RequestEnums.UploadType.UploadType;
    readonly projectId: LeoUUID;
    readonly documentId: LeoUUID;
    readonly isLive: boolean;
    readonly respectUniqueColumn: boolean;
  
  
    constructor(
    uploadType: RequestEnums.UploadType.UploadType, 
    projectId: LeoUUID, 
    documentId: LeoUUID, 
    isLive: boolean, 
    respectUniqueColumn: boolean
    ) {
      this.uploadType = uploadType;
      this.projectId = projectId;
      this.documentId = documentId;
      this.isLive = isLive;
      this.respectUniqueColumn = respectUniqueColumn;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const uploadType = RequestEnums.UploadType.fromDTO(getObject(dto, "uploadType", InvalidRequestUploadTypeError));
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const documentId = getLeoUuid(dto, "documentId", InvalidRequestDocumentIdError);
      const isLive = getBoolean(dto, "isLive", InvalidRequestIsLiveError);
      const respectUniqueColumn = getBoolean(dto, "respectUniqueColumn", InvalidRequestRespectUniqueColumnError);
      return new Request(
        uploadType,
        projectId,
        documentId,
        isLive,
        respectUniqueColumn
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          uploadType: this.uploadType.toDTO(),
          projectId: this.projectId.toDTO(),
          documentId: this.documentId.toDTO(),
          isLive: this.isLive,
          respectUniqueColumn: this.respectUniqueColumn
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `ValidateUniqueColumnAndUpdateRespondentRPC(uploadType='${this.uploadType}',projectId='${this.projectId}',documentId='${this.documentId}',isLive='${this.isLive}',respectUniqueColumn='${this.respectUniqueColumn}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `ValidateUniqueColumnAndUpdateRespondentRPC.Response()`;
  };

  export enum RPCError {
    InvalidFile = "INVALID_FILE",
    UnknownFile = "UNKNOWN_FILE",
    InvalidUniqueColumnName = "INVALID_UNIQUE_COLUMN_NAME",
    RespondentUpdateInProgress = "RESPONDENT_UPDATE_IN_PROGRESS",
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectArchived = "PROJECT_ARCHIVED",
    DuplicateRecordFound = "DUPLICATE_RECORD_FOUND",
    RespondentColumnLimitReached = "RESPONDENT_COLUMN_LIMIT_REACHED"
  }

  export namespace Errors {
    
    export interface InvalidFileDTO {
      code: string;
      invalidFileDetails: string;
    }
    
    export class InvalidFile extends LeoRPCError<RPCError> {
      readonly isInvalidFile: boolean = true;
    
      readonly invalidFileDetails: string;
    
      constructor(
      invalidFileDetails: string
      ) {
        const code = RPCError.InvalidFile;
        super(code);
        InvalidFile.validateInvalidFileDetails(invalidFileDetails);
        this.invalidFileDetails = invalidFileDetails;
      }
    
      static fromDTO(dto: InvalidFileDTO): InvalidFile {
        const invalidFileDetails = getString(dto, "invalidFileDetails", InvalidResponseInvalidFileInvalidFileDetailsError);
        return new InvalidFile(
          invalidFileDetails
        );
      }
    
      toDTO(): InvalidFileDTO {
        const dto: InvalidFileDTO = {
          code: this.code,
          invalidFileDetails: this.invalidFileDetails
        };
        return dto;
      }
    
      private static validateInvalidFileDetails = (invalidFileDetails: string): void => {
        if(invalidFileDetails.trim().length === 0) {
          throw new InvalidResponseInvalidFileInvalidFileDetailsNonEmptyError(
            `Attribute invalidFileDetails cannot be an empty string.`
          );
        }
      }
      
    }
    
    InvalidFile.prototype.toString = function toString(): string {
      return `ValidateUniqueColumnAndUpdateRespondentRPC.INVALID_FILE(invalidFileDetails='${this.invalidFileDetails}')`;
    };
    
    
    export interface UnknownFileDTO {
      code: string;
    }
    
    export class UnknownFile extends LeoRPCError<RPCError> {
      readonly isUnknownFile: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.UnknownFile;
        super(code);
      }
    
    
      toDTO(): UnknownFileDTO {
        const dto: UnknownFileDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    UnknownFile.prototype.toString = function toString(): string {
      return `ValidateUniqueColumnAndUpdateRespondentRPC.UNKNOWN_FILE()`;
    };
    
    
    export interface InvalidUniqueColumnNameDTO {
      code: string;
    }
    
    export class InvalidUniqueColumnName extends LeoRPCError<RPCError> {
      readonly isInvalidUniqueColumnName: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidUniqueColumnName;
        super(code);
      }
    
    
      toDTO(): InvalidUniqueColumnNameDTO {
        const dto: InvalidUniqueColumnNameDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidUniqueColumnName.prototype.toString = function toString(): string {
      return `ValidateUniqueColumnAndUpdateRespondentRPC.INVALID_UNIQUE_COLUMN_NAME()`;
    };
    
    
    export interface RespondentUpdateInProgressDTO {
      code: string;
    }
    
    export class RespondentUpdateInProgress extends LeoRPCError<RPCError> {
      readonly isRespondentUpdateInProgress: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.RespondentUpdateInProgress;
        super(code);
      }
    
    
      toDTO(): RespondentUpdateInProgressDTO {
        const dto: RespondentUpdateInProgressDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    RespondentUpdateInProgress.prototype.toString = function toString(): string {
      return `ValidateUniqueColumnAndUpdateRespondentRPC.RESPONDENT_UPDATE_IN_PROGRESS()`;
    };
    
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `ValidateUniqueColumnAndUpdateRespondentRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectArchivedDTO {
      code: string;
    }
    
    export class ProjectArchived extends LeoRPCError<RPCError> {
      readonly isProjectArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectArchived;
        super(code);
      }
    
    
      toDTO(): ProjectArchivedDTO {
        const dto: ProjectArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectArchived.prototype.toString = function toString(): string {
      return `ValidateUniqueColumnAndUpdateRespondentRPC.PROJECT_ARCHIVED()`;
    };
    
    
    export interface DuplicateRecordFoundDTO {
      code: string;
    }
    
    export class DuplicateRecordFound extends LeoRPCError<RPCError> {
      readonly isDuplicateRecordFound: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.DuplicateRecordFound;
        super(code);
      }
    
    
      toDTO(): DuplicateRecordFoundDTO {
        const dto: DuplicateRecordFoundDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    DuplicateRecordFound.prototype.toString = function toString(): string {
      return `ValidateUniqueColumnAndUpdateRespondentRPC.DUPLICATE_RECORD_FOUND()`;
    };
    
    
    export interface RespondentColumnLimitReachedDTO {
      code: string;
    }
    
    export class RespondentColumnLimitReached extends LeoRPCError<RPCError> {
      readonly isRespondentColumnLimitReached: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.RespondentColumnLimitReached;
        super(code);
      }
    
    
      toDTO(): RespondentColumnLimitReachedDTO {
        const dto: RespondentColumnLimitReachedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    RespondentColumnLimitReached.prototype.toString = function toString(): string {
      return `ValidateUniqueColumnAndUpdateRespondentRPC.RESPONDENT_COLUMN_LIMIT_REACHED()`;
    };
    

    export type Errors = InvalidFile | UnknownFile | InvalidUniqueColumnName | RespondentUpdateInProgress | InvalidProjectId | ProjectArchived | DuplicateRecordFound | RespondentColumnLimitReached;
    export type ErrorsDTO = InvalidFileDTO | UnknownFileDTO | InvalidUniqueColumnNameDTO | RespondentUpdateInProgressDTO | InvalidProjectIdDTO | ProjectArchivedDTO | DuplicateRecordFoundDTO | RespondentColumnLimitReachedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidFile:
          return InvalidFile.fromDTO(dto as InvalidFileDTO);
        case RPCError.UnknownFile:
          return new UnknownFile();
        case RPCError.InvalidUniqueColumnName:
          return new InvalidUniqueColumnName();
        case RPCError.RespondentUpdateInProgress:
          return new RespondentUpdateInProgress();
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectArchived:
          return new ProjectArchived();
        case RPCError.DuplicateRecordFound:
          return new DuplicateRecordFound();
        case RPCError.RespondentColumnLimitReached:
          return new RespondentColumnLimitReached();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestUploadTypeError extends ValidateUniqueColumnAndUpdateRespondentRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestUploadTypeError";
  }
}

export class InvalidRequestUploadTypeFirstError extends InvalidRequestUploadTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestUploadTypeFirstError";
  }
}

export class InvalidRequestUploadTypeFirstUniqueColumnError extends InvalidRequestUploadTypeFirstError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestUploadTypeFirstUniqueColumnError";
  }
}

export class InvalidRequestUploadTypeSubsequentError extends InvalidRequestUploadTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestUploadTypeSubsequentError";
  }
}

export class InvalidRequestProjectIdError extends ValidateUniqueColumnAndUpdateRespondentRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestDocumentIdError extends ValidateUniqueColumnAndUpdateRespondentRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestDocumentIdError";
  }
}

export class InvalidRequestIsLiveError extends ValidateUniqueColumnAndUpdateRespondentRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestIsLiveError";
  }
}

export class InvalidRequestRespectUniqueColumnError extends ValidateUniqueColumnAndUpdateRespondentRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestRespectUniqueColumnError";
  }
}


export class InvalidResponseInvalidFileError extends ValidateUniqueColumnAndUpdateRespondentRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidFileError";
  }
}

export class InvalidResponseInvalidFileInvalidFileDetailsError extends InvalidResponseInvalidFileError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidFileInvalidFileDetailsError";
  }
}

export class InvalidResponseInvalidFileInvalidFileDetailsNonEmptyError extends InvalidResponseInvalidFileInvalidFileDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidFileInvalidFileDetailsNonEmptyError";
  }
}
export class InvalidResponseUnknownFileError extends ValidateUniqueColumnAndUpdateRespondentRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseUnknownFileError";
  }
}

export class InvalidResponseInvalidUniqueColumnNameError extends ValidateUniqueColumnAndUpdateRespondentRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidUniqueColumnNameError";
  }
}

export class InvalidResponseRespondentUpdateInProgressError extends ValidateUniqueColumnAndUpdateRespondentRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentUpdateInProgressError";
  }
}

export class InvalidResponseInvalidProjectIdError extends ValidateUniqueColumnAndUpdateRespondentRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectArchivedError extends ValidateUniqueColumnAndUpdateRespondentRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectArchivedError";
  }
}

export class InvalidResponseDuplicateRecordFoundError extends ValidateUniqueColumnAndUpdateRespondentRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDuplicateRecordFoundError";
  }
}

export class InvalidResponseRespondentColumnLimitReachedError extends ValidateUniqueColumnAndUpdateRespondentRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentColumnLimitReachedError";
  }
}

